import { Alert } from "@/components/basic";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import useTimeZones from "@/hooks/useTimeZones";
import { findTimeZoneFromId, hasTimezoneMismatch } from "@/lib/utils";
import { useAuthenticated } from "@gettactic/hooks";
import Link from "next/link";

type TimeZoneSettingsLinkProps = {
  officeId: string;
  onClose?: () => void;
  embedded?: boolean;
  title?: string;
};

const TimeZoneSettingsLink: React.FC<TimeZoneSettingsLinkProps> = ({
  officeId,
  onClose = () => {},
  embedded = false,
  title
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const settingsPerms = !!authenticatedUser.permissions.settings;

  if (!settingsPerms) {
    return null;
  }

  return (
    <p className="text-sm">
      {title}
      {title ? ". " : ""}
      To change the office time zone, please{" "}
      <Link href={`/settings/offices/${officeId}`}>
        <a
          onClick={onClose}
          className="cursor-pointer text-primary-darker underline"
          target={embedded ? "_blank" : undefined}
          rel={embedded ? "noopener noreferrer" : undefined}
        >
          click here
        </a>
      </Link>
    </p>
  );
};

type TimeZoneMismatchProps = {
  officeId: string;
  timezone: string;
  className: string;
  onClose: () => void;
  embedded?: boolean;
};
export default function TimeZoneMismatchAlert({
  officeId,
  timezone,
  className = "",
  onClose,
  embedded = false
}: TimeZoneMismatchProps): JSX.Element {
  const show = hasTimezoneMismatch(timezone);
  const timezones = useTimeZones();
  const selectedTimeZone = findTimeZoneFromId(timezone, timezones) || null;
  const timezoneHumanName = selectedTimeZone
    ? selectedTimeZone.label
    : timezone;

  if (!show) {
    return <></>;
  }

  return (
    <div className={className}>
      <Alert
        variant="tacticAlert"
        canDismiss={false}
        alertTitle={`Times shown are in ${timezoneHumanName}`}
        alertMessage=""
        className="mt-2 px-4 py-2"
      >
        <TimeZoneSettingsLink
          officeId={officeId}
          onClose={onClose}
          embedded={embedded}
        />
      </Alert>
    </div>
  );
}

type TimeZoneMismatchTooltipProps = {
  resourceOfficeId: string;
  className?: string;
  onClose?: () => void;
  embedded?: boolean;
  short?: boolean;
};

export const TimeZoneMismatchTooltip = ({
  resourceOfficeId,
  className = "",
  onClose = () => {},
  embedded = false,
  short = false
}: TimeZoneMismatchTooltipProps): JSX.Element => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  // Get the office from the resource office ID
  const office = authenticatedUser.offices?.offices?.find(
    (office) => office.id === resourceOfficeId
  );

  if (!office) {
    return <span className={className}>Unknown Office</span>;
  }

  const timezone = office.time_zone;
  const show = hasTimezoneMismatch(timezone);
  const timezones = useTimeZones();
  const selectedTimeZone =
    findTimeZoneFromId(timezone, timezones, short) || null;
  const timezoneHumanName = selectedTimeZone
    ? selectedTimeZone.readableOffset
    : timezone;

  const title = selectedTimeZone ? selectedTimeZone.label : timezone;

  // If no mismatch, just return the timezone name as text
  if (!show) {
    return <span className={className}>{timezoneHumanName}</span>;
  }

  // If there's a mismatch, show tooltip with alert content
  return (
    <TooltipProvider>
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <span className={`cursor-pointer font-medium ${className}`}>
            {timezoneHumanName}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs p-0" side="top">
          <div className="break-words whitespace-pre-line p-4" style={{ wordBreak: "break-word", maxWidth: "20rem" }}>
            <TimeZoneSettingsLink
              officeId={resourceOfficeId}
              onClose={onClose}
              embedded={embedded}
              title={`Times shown are in ${title}`}
            />
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
